/**
 * Shared Vooruitzicht carousel (Swiper).
 *
 * Owned by vooruitzicht-theme-core. Themes customize via:
 * - data-carousel-* attributes on markup
 * - window.vtcCarousel.presets (localized from Theme_Config / vtc/carousel/presets)
 * - theme CSS for chrome (nav size, colors) — not this file
 *
 * Swiper layout CSS is enqueued separately (dist/css/carousel.css); do not import
 * swiper/css from JS (broken Vite chunk URLs in WordPress).
 */

import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

class CarouselBase {
	constructor() {
		this.carousels = [];
		this.configPresets = this.getConfigPresets();
		this.init();
	}
	
	/**
	 * Default presets, shallow-merged with theme overrides from window.vtcCarousel.presets.
	 */
	getConfigPresets() {
		const defaults = {
			// Product Collections Carousel
			'product-collections': {
				slidesPerView: {
					mobile: 1.5,
					tablet: 2.5,
					desktop: 4.5,
				},
				spaceBetween: {
					mobile: 16,
					tablet: 20,
					desktop: 24,
				},
				disableWhenAllVisible: true,
				loop: false,
				autoplay: false,
				pagination: false,
				navigation: false
			},
			
			// Example: Hero Carousel
			'hero': {
				slidesPerView: {
					mobile: 1,
					tablet: 1,
					desktop: 1,
				},
				spaceBetween: {
					mobile: 0,
					tablet: 0,
					desktop: 0,
				},
				disableWhenAllVisible: false,
				loop: true,
				autoplay: 5000, // 5 seconds
			},
			
			// Example: Testimonials Carousel
			'testimonials': {
				slidesPerView: {
					mobile: 1,
					tablet: 2,
					desktop: 3,
				},
				spaceBetween: {
					mobile: 16,
					tablet: 20,
					desktop: 24,
				},
				disableWhenAllVisible: true,
				loop: false,
				autoplay: false,
			},

			// Related news cards (single post block) — matches project/building grid: 1 / 2 / 2 / 3 cols, gap --spacing--lg
			'related-news': {
				slidesPerView: {
					mobile: 1,
					tablet: 2,
					desktop: 2,
					wide: 3,
				},
				spaceBetween: {
					mobile: 40,
					tablet: 40,
					desktop: 40,
					wide: 40,
				},
				disableWhenAllVisible: true,
				loop: false,
				autoplay: false,
				pagination: false,
				navigation: true,
			},

			// Property offering highlight carousel (marketing cards).
			'property-offering': {
				slidesPerView: {
					mobile: 1,
					tablet: 2,
					desktop: 2,
					wide: 3,
				},
				spaceBetween: {
					mobile: 40,
					tablet: 40,
					desktop: 40,
					wide: 40,
				},
				disableWhenAllVisible: true,
				loop: false,
				autoplay: false,
				pagination: false,
				navigation: true,
			},
			
			// Default configuration
			'default': {
				slidesPerView: {
					mobile: 1.5,
					tablet: 2.5,
					desktop: 3.5,
				},
				spaceBetween: {
					mobile: 16,
					tablet: 20,
					desktop: 24,
				},
				disableWhenAllVisible: true,
				loop: false,
				autoplay: false,
				pagination: false,
				navigation: false
			},

			// FacetWP property card image gallery (featured + ACF gallery).
			'property-card': {
				slidesPerView: {
					mobile: 1,
					tablet: 1,
					desktop: 1,
					wide: 1,
				},
				spaceBetween: {
					mobile: 0,
					tablet: 0,
					desktop: 0,
					wide: 0,
				},
				disableWhenAllVisible: false,
				loop: false,
				autoplay: false,
				pagination: true,
				navigation: true,
			},

			// Two-column text carousel: single image per view, swipe + pagination dots.
			'two-column-text-carousel': {
				slidesPerView: {
					mobile: 1,
					tablet: 1,
					desktop: 1,
					wide: 1,
				},
				spaceBetween: {
					mobile: 0,
					tablet: 0,
					desktop: 0,
					wide: 0,
				},
				disableWhenAllVisible: false,
				loop: false,
				autoplay: false,
				pagination: true,
				navigation: true,
			},

			// Extended timeline block: one entry image per view, pagination + prev/next when multiple slides.
			'extended-timeline': {
				slidesPerView: {
					mobile: 1,
					tablet: 1,
					desktop: 1,
					wide: 1,
				},
				spaceBetween: {
					mobile: 0,
					tablet: 0,
					desktop: 0,
					wide: 0,
				},
				disableWhenAllVisible: false,
				loop: false,
				autoplay: false,
				pagination: true,
				navigation: true,
			},
		};

		const overrides =
			typeof window !== 'undefined' && window.vtcCarousel && window.vtcCarousel.presets
				? window.vtcCarousel.presets
				: {};

		return { ...defaults, ...overrides };
	}
	
	init() {
		// Wait for DOM to be ready
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.initCarousels());
		} else {
			this.initCarousels();
		}

		document.addEventListener('facetwp-loaded', () => this.initCarousels());
	}
	
	initCarousels() {
		const carouselElements = document.querySelectorAll('[data-carousel]');
		
		carouselElements.forEach((element) => {
			this.initCarousel(element);
		});
	}
	
	/**
	 * Get configuration for a carousel element
	 * Priority: data attributes > preset config > default config
	 */
	getConfig(element) {
		// Get preset config name or use default
		const presetName = element.dataset.carouselConfig || 'default';
		const preset = this.configPresets[presetName] || this.configPresets.default;
		const spv = preset.slidesPerView;
		const sb = preset.spaceBetween;
		const wideSlides =
			element.dataset.carouselWide !== undefined
				? parseFloat(element.dataset.carouselWide)
				: (spv.wide !== undefined ? spv.wide : spv.desktop);
		const wideSpace =
			element.dataset.carouselSpaceWide !== undefined
				? parseInt(element.dataset.carouselSpaceWide, 10)
				: (sb.wide !== undefined ? sb.wide : sb.desktop);

		// Build config from preset and override with data attributes
		const config = {
			slidesPerView: {
				mobile: parseFloat(element.dataset.carouselMobile) || spv.mobile,
				tablet: parseFloat(element.dataset.carouselTablet) || spv.tablet,
				desktop: parseFloat(element.dataset.carouselDesktop) || spv.desktop,
				wide: wideSlides,
			},
			spaceBetween: {
				mobile: parseInt(element.dataset.carouselSpaceMobile, 10) || sb.mobile,
				tablet: parseInt(element.dataset.carouselSpaceTablet, 10) || sb.tablet,
				desktop: parseInt(element.dataset.carouselSpaceDesktop, 10) || sb.desktop,
				wide: wideSpace,
			},
			disableWhenAllVisible: element.dataset.carouselDisableWhenAllVisible !== undefined
				? element.dataset.carouselDisableWhenAllVisible === 'true'
				: preset.disableWhenAllVisible,
			loop: element.dataset.carouselLoop !== undefined
				? element.dataset.carouselLoop === 'true'
				: preset.loop,
			autoplay: element.dataset.carouselAutoplay !== undefined
				? parseInt(element.dataset.carouselAutoplay)
				: preset.autoplay,
		};
		
		return config;
	}
	
	/**
	 * Root for nav/pagination (supports controls outside the .swiper element).
	 *
	 * @param {HTMLElement} element Swiper container ([data-carousel]).
	 * @returns {HTMLElement}
	 */
	getCarouselChromeRoot(element) {
		return element.closest('[data-carousel-root]') || element;
	}

	initCarousel(element) {
		if (element.dataset.carouselInitialized === 'true') {
			return;
		}

		const config = this.getConfig(element);
		const slides = element.querySelectorAll('.swiper-slide');
		const slideCount = slides.length;
		const chromeRoot = this.getCarouselChromeRoot(element);

		const paginationEl = chromeRoot.querySelector('.swiper-pagination');
		const nextEl = chromeRoot.querySelector('.swiper-button-next');
		const prevEl = chromeRoot.querySelector('.swiper-button-prev');
		const modules = [];
		if (paginationEl) {
			modules.push(Pagination);
		}
		if (nextEl && prevEl) {
			modules.push(Navigation);
		}
		
		const breakpoints = {
			768: {
				slidesPerView: config.slidesPerView.tablet,
				spaceBetween: config.spaceBetween.tablet,
			},
			1024: {
				slidesPerView: config.slidesPerView.desktop,
				spaceBetween: config.spaceBetween.desktop,
			},
		};
		if (
			config.slidesPerView.wide !== config.slidesPerView.desktop ||
			config.spaceBetween.wide !== config.spaceBetween.desktop
		) {
			breakpoints[1440] = {
				slidesPerView: config.slidesPerView.wide,
				spaceBetween: config.spaceBetween.wide,
			};
		}

		// Build Swiper configuration
		const swiperConfig = {
			modules,
			slidesPerView: config.slidesPerView.mobile,
			spaceBetween: config.spaceBetween.mobile,
			loop: config.loop,
			breakpoints,
			on: {
				init: (swiper) => {
					if (config.disableWhenAllVisible) {
						this.checkCarouselNeeded(swiper, element, slideCount, config);
					}
				},
				resize: (swiper) => {
					if (config.disableWhenAllVisible) {
						this.checkCarouselNeeded(swiper, element, slideCount, config);
					}
				},
			},
		};

		if (paginationEl) {
			swiperConfig.pagination = {
				el: paginationEl,
				clickable: true,
			};
		}

		if (nextEl && prevEl) {
			swiperConfig.navigation = {
				nextEl,
				prevEl,
			};
		}
		
		// Add autoplay if configured
		if (config.autoplay) {
			swiperConfig.modules.push(Autoplay);
			swiperConfig.autoplay = {
				delay: config.autoplay,
				disableOnInteraction: false,
			};
		}
		
		// Initialize Swiper
		const swiper = new Swiper(element, swiperConfig);

		element.dataset.carouselInitialized = 'true';
		
		this.carousels.push(swiper);
	}
	
	/**
	 * Check if carousel should be disabled when all slides are visible
	 */
	checkCarouselNeeded(swiperInstance, element, slideCount, config) {
		const currentBreakpoint = swiperInstance.currentBreakpoint || 'max';
		let slidesPerView;
		
		// Determine slidesPerView based on current breakpoint
		if (currentBreakpoint === 'max' || !swiperInstance.params.breakpoints[currentBreakpoint]) {
			slidesPerView = swiperInstance.params.slidesPerView;
		} else {
			slidesPerView = swiperInstance.params.breakpoints[currentBreakpoint].slidesPerView;
		}
		
		// Parse slidesPerView if it's a string
		slidesPerView = parseFloat(slidesPerView);
		
		// If all slides fit in the viewport, disable carousel features
		if (slideCount <= Math.floor(slidesPerView)) {
			this.disableCarousel(swiperInstance, element);
		} else {
			this.enableCarousel(swiperInstance, element);
		}
	}
	
	/**
	 * Disable carousel features
	 */
	disableCarousel(swiperInstance, element) {
		const chromeRoot = this.getCarouselChromeRoot(element);

		// Disable navigation
		if (swiperInstance.navigation) {
			swiperInstance.navigation.destroy();
		}
		
		// Disable pagination
		if (swiperInstance.pagination) {
			swiperInstance.pagination.destroy();
		}
		
		// Hide navigation buttons
		const navNext = chromeRoot.querySelector('.swiper-button-next');
		const navPrev = chromeRoot.querySelector('.swiper-button-prev');
		const pagination = chromeRoot.querySelector('.swiper-pagination');
		
		if (navNext) navNext.style.display = 'none';
		if (navPrev) navPrev.style.display = 'none';
		if (pagination) pagination.style.display = 'none';
		
		// Disable dragging
		swiperInstance.allowTouchMove = false;
		
		// Add class to indicate carousel is disabled
		element.classList.add('swiper-disabled');
	}
	
	/**
	 * Enable carousel features
	 */
	enableCarousel(swiperInstance, element) {
		const chromeRoot = this.getCarouselChromeRoot(element);

		// Re-enable navigation if it was disabled
		if (swiperInstance.params.navigation && !swiperInstance.navigation) {
			swiperInstance.navigation.init();
			swiperInstance.navigation.update();
		}
		
		// Re-enable pagination if it was disabled
		if (swiperInstance.params.pagination && !swiperInstance.pagination) {
			swiperInstance.pagination.init();
			swiperInstance.pagination.render();
			swiperInstance.pagination.update();
		}
		
		// Show navigation buttons
		const navNext = chromeRoot.querySelector('.swiper-button-next');
		const navPrev = chromeRoot.querySelector('.swiper-button-prev');
		const pagination = chromeRoot.querySelector('.swiper-pagination');
		
		if (navNext) navNext.style.display = '';
		if (navPrev) navPrev.style.display = '';
		if (pagination) pagination.style.display = '';
		
		// Enable dragging
		swiperInstance.allowTouchMove = true;
		
		// Remove disabled class
		element.classList.remove('swiper-disabled');
	}
	
	/**
	 * Destroy all carousels
	 */
	destroy() {
		this.carousels.forEach((swiper) => {
			if (swiper && swiper.destroy) {
				swiper.destroy(true, true);
			}
		});
		this.carousels = [];
	}
}

const carouselBase = new CarouselBase();

window.vtcCarouselBase = carouselBase;

export default CarouselBase;
